import React, { useState, useEffect } from 'react';
import { FileText, Eye, X } from 'lucide-react';

const HomeworkReviewInterface = ({ homework, onClose }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, [homework.id]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/homework/student/they/${homework.id}/submissions`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch submissions: ${response.status}`);
      }

      const submissionsData = await response.json();
      setSubmissions(submissionsData);
    } catch (err) {
      console.error('Error fetching submissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewSubmission = async (submissionId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/homework/student/they/submissions/${submissionId}/image`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      
      const newWindow = window.open();
      newWindow.document.write(`
        <html>
          <head><title>Submission Image</title></head>
          <body style="margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5;">
            <img src="${imageUrl}" style="max-width: 100%; max-height: 100vh;" alt="Submission" />
          </body>
        </html>
      `);
    } catch (err) {
      console.error('Error viewing submission:', err);
      setError('Failed to view submission: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-white shadow-2xl rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Homework Details
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {homework.subject}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/50 transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Assignment Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subject:</span>
                  <span className="font-medium">{homework.subject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">{formatDate(homework.dueDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium px-2 py-1 rounded text-xs ${
                    homework.status === 'GRADED' || homework.status === 'Completed' ? 'bg-green-100 text-green-800' :
                    homework.status === 'Late' ? 'bg-red-100 text-red-800' :
                    homework.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {homework.status || 'Assigned'}
                  </span>
                </div>
                {homework.score && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Score:</span>
                    <span className="font-bold text-green-600">{homework.score}/100</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {homework.description || 'No description provided.'}
              </p>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-4 text-lg">Your Submissions</h3>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading submissions...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-red-800">{error}</p>
                <button
                  onClick={fetchSubmissions}
                  className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Retry
                </button>
              </div>
            ) : submissions.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                <FileText className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                <h4 className="font-medium text-yellow-900 mb-1">No Submissions Yet</h4>
                <p className="text-yellow-800 text-sm">
                  You haven't submitted any work for this assignment yet.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {submissions.map((submission, index) => (
                  <div key={submission.id} className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow">
                    <div className="bg-gray-100 p-3 border-b">
                      <h4 className="font-medium text-sm text-gray-900 truncate">
                        {submission.fileName || `Submission ${index + 1}`}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">
                        Submitted: {formatDate(submission.createdAt)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Type: {submission.contentType || 'Image'}
                      </p>
                    </div>
                    <div className="p-4">
                      <button
                        onClick={() => handleViewSubmission(submission.id)}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center text-sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Image
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
        </div>

        <div className="border-t bg-gray-50 p-6">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeworkReviewInterface;